<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { useModal } from '@tundr/modal';
  import { useSidePanelStore } from '@tundr/side-panel';
  import { useIsFormDirty, useSubmitCount } from 'vee-validate';
  import {
    computed,
    onBeforeUnmount,
    onMounted,
    onUnmounted,
    watchEffect,
  } from 'vue';
  import { onBeforeRouteLeave } from 'vue-router';
  import { useNavigationGuardStore } from '../../store/navigation-guard.store';

  const navigationGuardStore = useNavigationGuardStore();
  const { setIsBlocked } = navigationGuardStore;
  const sidePanelStore = useSidePanelStore();
  const { openConfirmModal } = useModal();
  const { t } = useLocale('common');

  defineProps<{
    confirmExitTitle?: string;
    confirmExitDescription?: string;
  }>();

  const isFormDirty = useIsFormDirty();
  const submitCount = useSubmitCount();

  const isFormSubmitted = computed(() => submitCount.value > 0);

  // If the form is dirty and not submitted, the navigation outside is blocked
  const isFormBlocked = computed(
    () => isFormDirty.value && !isFormSubmitted.value,
  );

  const isSidePanelOpen = computed(() => sidePanelStore.isSidePanelOpen);

  watchEffect(() => {
    setIsBlocked(isFormBlocked.value);
  });

  onUnmounted(() => {
    setIsBlocked(false);
    navigationGuardStore.setIsExitConfirmed(false);
  });

  onBeforeRouteLeave((_to, _from, next) => {
    if (
      isFormBlocked.value &&
      !isSidePanelOpen.value &&
      !navigationGuardStore.isExitConfirmed
    ) {
      openConfirmModal({
        title: t('confirm_exit.title'),
        message: t('confirm_exit.description'),
        config: { blocking: true },
        labels: {
          confirm: t('actions.exit'),
          cancel: t('actions.cancel'),
        },
        events: {
          onConfirm: () => {
            setIsBlocked(false);
            next(true);
          },
          onCancel: () => next(false),
        },
      });
      return;
    }
    next();
  });

  // If the user tries to close the browser, show a prompt to confirm the exit
  const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (isFormBlocked.value) {
      // Standard way to trigger browser's native warning dialog
      event.preventDefault();
      // Some browsers require returnValue to be set
      event.returnValue = '';
      return '';
    }
  };

  // Add beforeunload listener when component mounts
  onMounted(() => {
    window.addEventListener('beforeunload', beforeUnloadHandler);
  });

  // Cleanup on unmount
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  });
</script>

<template>
  <span />
</template>
